import { actionKey } from './constants';
import {
  uploadFile,
  uploadFiles,
  uploadProgress,
  uploadSuccess,
  uploadFailure,
  uploadFileImmediately,
  cancelFileUpload,
  clearInactiveUploads,
  uploadDocument,
  uploadDocumentProgress,
  uploadDocumentSuccess,
  uploadDocumentFailure,
  cancelDocumentUpload,
  uploadAttachment,
  uploadAttachmentImmediately,
  uploadAttachmentFailure,
  uploadAttachmentProgress,
  uploadAttachmentSuccess,
  cancelAttachmentUpload,
  attachmentDeviationUnlock,
  attachmentDeviationLock,
} from './actions';
import { UploadState } from './types';
import { handleActions } from 'redux-actions';

const defaultState: UploadState = {
  fileUploads: [],
  fileUploadsWaiting: [],
  fileUploadsCompleted: [],
  attachmentUploads: [],
  attachmentUploadsWaiting: [],
  attachmentUploadsCompleted: [],
  attachmentDeviationLocked: {},
  documentUploads: {
    narf: undefined,
    sprf: undefined,
  },
};

const actionHandlers = handleActions<UploadState, any>(
  {
    [uploadFile.toString()]: (state, { payload }: ReturnType<typeof uploadFile>) => ({
      ...state,
      fileUploadsWaiting: [...state.fileUploadsWaiting, { ...payload }],
    }),
    [uploadFiles.toString()]: (state, { payload }: ReturnType<typeof uploadFiles>) => ({
      ...state,
      fileUploadsWaiting: [...state.fileUploadsWaiting, ...payload.files],
    }),
    [uploadFileImmediately.toString()]: (
      state,
      { payload }: ReturnType<typeof uploadFileImmediately>
    ) => ({
      ...state,
      fileUploads: [...state.fileUploads, { ...payload }],
      fileUploadsWaiting: state.fileUploadsWaiting.filter(f => f.uploadId !== payload.uploadId),
    }),
    [uploadFailure.toString()]: (
      state,
      { payload: { uploadId, error } }: ReturnType<typeof uploadFailure>
    ) => ({
      ...state,
      fileUploads: [...state.fileUploads].map(item =>
        item.uploadId === uploadId ? { ...item, error } : item
      ),
    }),
    [uploadProgress.toString()]: (
      state,
      { payload: { uploadId, progress } }: ReturnType<typeof uploadProgress>
    ) => ({
      ...state,
      fileUploads: [...state.fileUploads].map(item =>
        item.uploadId === uploadId ? { ...item, progress } : item
      ),
    }),
    [uploadSuccess.toString()]: (state, { payload }: ReturnType<typeof uploadSuccess>) => {
      const fileUploadInfo = state.fileUploads.find(item => payload.uploadId === item.uploadId);
      if (!fileUploadInfo) {
        return state;
      }
      return {
        ...state,
        fileUploads: state.fileUploads.filter(item => payload.uploadId !== item.uploadId),
        fileUploadsCompleted: [
          { ...fileUploadInfo, deviationId: payload.deviationId },
          ...state.fileUploadsCompleted,
        ],
      };
    },
    [cancelFileUpload.toString()]: (
      state,
      { payload: { uploadId } }: ReturnType<typeof cancelFileUpload>
    ) => ({
      ...state,
      fileUploads: state.fileUploads.filter(item => item.uploadId !== uploadId),
      fileUploadsWaiting: state.fileUploadsWaiting.filter(item => item.uploadId !== uploadId),
    }),
    [clearInactiveUploads.toString()]: state => ({
      ...state,
      fileUploads: state.fileUploads.filter(item => !item.error),
      fileUploadsCompleted: [],
    }),
    [uploadAttachment.toString()]: (state, { payload }: ReturnType<typeof uploadAttachment>) => ({
      ...state,
      attachmentUploadsWaiting: [...state.attachmentUploadsWaiting, { ...payload }],
    }),
    [uploadAttachmentImmediately.toString()]: (
      state,
      { payload }: ReturnType<typeof uploadAttachmentImmediately>
    ) => ({
      ...state,
      attachmentUploads: [...state.attachmentUploads, { ...payload }],
      attachmentUploadsWaiting: state.attachmentUploadsWaiting.filter(
        f => f.uploadId !== payload.uploadId
      ),
    }),
    [uploadAttachmentFailure.toString()]: (
      state,
      { payload: { uploadId, error } }: ReturnType<typeof uploadAttachmentFailure>
    ) => ({
      ...state,
      attachmentUploads: [...state.attachmentUploads].map(item =>
        item.uploadId === uploadId ? { ...item, error } : item
      ),
    }),
    [uploadAttachmentProgress.toString()]: (
      state,
      { payload: { uploadId, progress } }: ReturnType<typeof uploadAttachmentProgress>
    ) => ({
      ...state,
      attachmentUploads: [...state.attachmentUploads].map(item =>
        item.uploadId === uploadId ? { ...item, progress } : item
      ),
    }),
    [uploadAttachmentSuccess.toString()]: (
      state,
      { payload }: ReturnType<typeof uploadAttachmentSuccess>
    ) => {
      const attachmentUploadInfo = state.attachmentUploads.find(
        item => payload.uploadId === item.uploadId
      );
      if (!attachmentUploadInfo) {
        return state;
      }
      return {
        ...state,
        attachmentUploads: state.attachmentUploads.filter(
          item => payload.uploadId !== item.uploadId
        ),
        attachmentUploadsCompleted: [attachmentUploadInfo, ...state.attachmentUploadsCompleted],
      };
    },
    [cancelAttachmentUpload.toString()]: (
      state,
      { payload: { uploadId } }: ReturnType<typeof cancelAttachmentUpload>
    ) => ({
      ...state,
      attachmentUploads: state.attachmentUploads.filter(item => item.uploadId !== uploadId),
      attachmentUploadsWaiting: state.attachmentUploadsWaiting.filter(
        item => item.uploadId !== uploadId
      ),
    }),
    [uploadDocument.toString()]: (state, { payload }: ReturnType<typeof uploadDocument>) => ({
      ...state,
      documentUploads: {
        ...state.documentUploads,
        [payload.documentType]: payload,
      },
    }),
    [uploadDocumentProgress.toString()]: (
      state,
      { payload: { documentType, progress } }: ReturnType<typeof uploadDocumentProgress>
    ) => ({
      ...state,
      documentUploads: {
        ...state.documentUploads,
        [documentType]: {
          ...state.documentUploads[documentType],
          progress,
        },
      },
    }),
    [uploadDocumentSuccess.toString()]: (
      state,
      { payload: { documentType, documentUrl } }: ReturnType<typeof uploadDocumentSuccess>
    ) => {
      return {
        ...state,
        documentUploads: {
          ...state.documentUploads,
          [documentType]: {
            ...state.documentUploads[documentType],
            documentUrl,
          },
        },
      };
    },
    [uploadDocumentFailure.toString()]: (
      state,
      { payload: { documentType, error } }: ReturnType<typeof uploadDocumentFailure>
    ) => ({
      ...state,
      documentUploads: {
        ...state.documentUploads,
        [documentType]: {
          ...state.documentUploads[documentType],
          error,
        },
      },
    }),
    [cancelDocumentUpload.toString()]: (
      state,
      { payload: { documentType } }: ReturnType<typeof cancelDocumentUpload>
    ) => ({
      ...state,
      documentUploads: {
        ...state.documentUploads,
        [documentType]: undefined,
      },
    }),
    [attachmentDeviationUnlock.toString()]: (
      state,
      { payload: { deviationId } }: ReturnType<typeof attachmentDeviationUnlock>
    ) => ({
      ...state,
      attachmentDeviationLocked: {
        ...state.attachmentDeviationLocked,
        [deviationId]: false,
      },
    }),
    [attachmentDeviationLock.toString()]: (
      state,
      { payload: { deviationId } }: ReturnType<typeof attachmentDeviationLock>
    ) => ({
      ...state,
      attachmentDeviationLocked: {
        ...state.attachmentDeviationLocked,
        [deviationId]: true,
      },
    }),
  },
  defaultState
);

const reducers = {
  [actionKey as string]: actionHandlers,
};

export default reducers;
